import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Checkbox,
  Stack,
  Container,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

const PRIMARY = '#6366f1';

function HistoryPage() {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Selection and editing
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editingCategory, setEditingCategory] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [txnResponse, catResponse] = await Promise.all([
        api.get('/api/transactions'),
        api.get('/api/categories'),
      ]);
      setTransactions(txnResponse.data.transactions || []);
      setCategories(catResponse.data.categories || []);
      setError(null);
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    const currentPageIds = paginatedTransactions.map((t) => t.id);
    const allCurrentSelected = currentPageIds.every((id) => selectedIds.has(id));
    const newSelected = new Set(selectedIds);
    if (allCurrentSelected && currentPageIds.length > 0) {
      currentPageIds.forEach((id) => newSelected.delete(id));
    } else {
      currentPageIds.forEach((id) => newSelected.add(id));
    }
    setSelectedIds(newSelected);
  };

  // Delete handlers
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected transaction(s)?`)) return;
    try {
      await api.post('/api/transactions/bulk-delete', {
        transaction_ids: Array.from(selectedIds),
      });
      setSelectedIds(new Set());
      await fetchData();
    } catch (err) {
      alert('Failed to delete transactions');
      console.error(err);
    }
  };

  // Edit handlers
  const startEdit = (transaction) => {
    setEditingId(transaction.id);
    setEditingCategory(transaction.category);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingCategory('');
  };

  const saveEdit = async (transactionId) => {
    try {
      await api.post(`/api/transactions/${transactionId}/category`, {
        category: editingCategory,
      });
      setEditingId(null);
      setEditingCategory('');
      await fetchData();
    } catch (err) {
      alert('Failed to update category');
      console.error(err);
    }
  };

  // Filter and sort
  const getFilteredTransactions = () => {
    let filtered = [...transactions];
    if (filterType === 'debits') {
      filtered = filtered.filter((t) => t.amount > 0);
    } else if (filterType === 'credits') {
      filtered = filtered.filter((t) => t.amount < 0);
    }
    if (filterCategory !== 'all') {
      filtered = filtered.filter((t) => t.category === filterCategory);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.merchant?.toLowerCase().includes(term) ||
          t.description?.toLowerCase().includes(term)
      );
    }
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'amount-desc':
          return Math.abs(b.amount) - Math.abs(a.amount);
        case 'amount-asc':
          return Math.abs(a.amount) - Math.abs(b.amount);
        case 'merchant':
          return (a.merchant || '').localeCompare(b.merchant || '');
        default:
          return 0;
      }
    });
    return filtered;
  };

  const filteredTransactions = getFilteredTransactions();
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterType, sortBy]);

  // Summary stats
  const totalDebits = filteredTransactions
    .filter((t) => t.amount > 0)
    .reduce((sum, txn) => sum + txn.amount, 0);
  const totalCredits = filteredTransactions
    .filter((t) => t.amount < 0)
    .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
  const netAmount = totalDebits - totalCredits;

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary" fontSize={18}>Loading transactions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Transaction History
          </Typography>
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {filteredTransactions.length} transactions
            </Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">
              Debits: ${totalDebits.toFixed(2)}
            </Typography>
            <Typography variant="body2" fontWeight={600} color="success.main">
              Credits: ${totalCredits.toFixed(2)}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              Net: ${netAmount.toFixed(2)}
            </Typography>
          </Stack>
        </Stack>

        {/* Filter Bar */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', borderRadius: 2 }}>
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              placeholder="Search merchant or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 220 }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={filterType}
                label="Transaction Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Transactions</MenuItem>
                <MenuItem value="debits">Debits Only (Charges)</MenuItem>
                <MenuItem value="credits">Credits Only (Payments/Refunds)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="date-desc">Date (Newest)</MenuItem>
                <MenuItem value="date-asc">Date (Oldest)</MenuItem>
                <MenuItem value="amount-desc">Amount (High to Low)</MenuItem>
                <MenuItem value="amount-asc">Amount (Low to High)</MenuItem>
                <MenuItem value="merchant">Merchant (A-Z)</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Bulk Actions */}
          {selectedIds.size > 0 && (
            <Stack direction="row" alignItems="center" gap={2} mt={2} pt={2} borderTop="1px solid" borderColor="divider">
              <Typography variant="body2" fontWeight={600}>
                {selectedIds.size} selected
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected
              </Button>
            </Stack>
          )}
        </Paper>

        {/* Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', borderRadius: 2, mb: 2 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    size="small"
                    checked={
                      paginatedTransactions.length > 0 &&
                      paginatedTransactions.every((t) => selectedIds.has(t.id))
                    }
                    indeterminate={
                      paginatedTransactions.some((t) => selectedIds.has(t.id)) &&
                      !paginatedTransactions.every((t) => selectedIds.has(t.id))
                    }
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Merchant</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((txn) => (
                <TableRow
                  key={txn.id}
                  selected={selectedIds.has(txn.id)}
                  hover
                  sx={{
                    bgcolor: selectedIds.has(txn.id) ? 'rgba(99,102,241,0.06)' : 'inherit',
                    '&.Mui-selected': { bgcolor: 'rgba(99,102,241,0.08)' },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(txn.id)}
                      onChange={() => toggleSelection(txn.id)}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 13 }}>
                    {new Date(txn.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    <Tooltip title={txn.merchant || ''} placement="top">
                      <span>{txn.merchant}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 13 }}>
                    <Tooltip title={txn.description || ''} placement="top">
                      <span>{txn.description}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: txn.amount < 0 ? 'success.main' : 'error.main', whiteSpace: 'nowrap' }}>
                    {txn.amount < 0 ? '-' : ''}${Math.abs(txn.amount).toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    {editingId === txn.id ? (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={editingCategory}
                          onChange={(e) => setEditingCategory(e.target.value)}
                          autoFocus
                          sx={{ fontSize: 13 }}
                        >
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.name} sx={{ fontSize: 13 }}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={txn.category}
                        size="small"
                        sx={{ bgcolor: 'rgba(99,102,241,0.1)', color: PRIMARY, fontWeight: 500, fontSize: 12 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {editingId === txn.id ? (
                      <Stack direction="row" gap={0.5}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="success" onClick={() => saveEdit(txn.id)}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" color="default" onClick={cancelEdit}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Tooltip title="Edit category">
                        <IconButton size="small" onClick={() => startEdit(txn)} sx={{ color: PRIMARY }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Empty state */}
        {filteredTransactions.length === 0 && (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', borderRadius: 2 }}>
            <Typography color="text.secondary" mb={2}>No transactions found</Typography>
            {(searchTerm || filterCategory !== 'all' || filterType !== 'all') && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setFilterCategory('all');
                  setFilterType('all');
                }}
                sx={{ borderColor: PRIMARY, color: PRIMARY }}
              >
                Clear Filters
              </Button>
            )}
          </Paper>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Stack alignItems="center" gap={1} mt={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root.Mui-selected': { bgcolor: PRIMARY, color: '#fff' },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Showing {startIndex + 1}–{Math.min(endIndex, filteredTransactions.length)} of {filteredTransactions.length}
            </Typography>
          </Stack>
        )}
      </Container>
    </Box>
  );
}

export default HistoryPage;
